import React from 'react'
import { Alert, AlertTitle, Grid, ListItem, Paper, Popper, PopperProps, Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { Administration, IRevenueGroupAdministration, Ledger, VatType } from '@cbgms/api'
import { useTranslation } from 'react-i18next'
import { useEnumTranslation } from '@cbgms/base/enums/use-enum-translation'
import { sortVATTypes } from './utils'
import { Autocomplete } from '@carsys/form'
import { EmptyUUID } from '@cbgms/base/utils/uuid'
import { WorkorderInvoiceType } from '@cbgms/api/modules/enums/workorder-invoice-type'

interface IProps {
  tabIndex: number
}

export const FallbackForDepartments: React.FC<IProps> = ({ tabIndex }) => {
  const {
    values: { Administrations, VatLimiter },
    setFieldValue
  } = useFormikContext<IRevenueGroupAdministration>()
  const { t } = useTranslation()
  const { te } = useEnumTranslation()

  const autocompleteLedgerDefaultProps = {
    freeSolo: true,
    autoSelect: true,
    options:
      Administrations[tabIndex].LedgerAccounts?.map(item => ({
        text: item.Code,
        value: item.Code,
        label: `${item.Code} ${item.Description}`
      })) || [],
    renderOption: (props: any, option: any) => (
      <ListItem key={props.key}>{option?.label || option?.text || option?.value || option}</ListItem>
    ),
    PopperComponent: (props: PopperProps) => {
      return (
        <Popper {...props} placement='bottom-start' style={{ width: 'fit-content', minWidth: props?.style?.width, maxWidth: '80vh' }} />
      )
    }
  }

  const setValidationOnFields = (type: keyof Ledger, value: string, fallbackIndex: number, vatType: VatType) => {
    const allAdministration: Administration | undefined = Administrations.find(Administration => Administration.UUID === EmptyUUID)
    const allDefaultLedger = allAdministration?.Ledgers.find(ledger => ledger.DepartmentUUID === EmptyUUID || ledger.DepartmentUUID === '')

    if (Administrations[tabIndex].Ledgers[fallbackIndex].AdministrationUUID === EmptyUUID) {
      for (let adminIndex = 0; adminIndex < Administrations.length; adminIndex++) {
        for (let ledgerIndex = 0; ledgerIndex < Administrations[adminIndex].Ledgers.length; ledgerIndex++) {
          const indexedLedger = Administrations[adminIndex].Ledgers[ledgerIndex]
          const fallbackLedger = Administrations[adminIndex].Ledgers[fallbackIndex]
          const allAdministrationLedger = allAdministration?.Ledgers[ledgerIndex]

          if (
            indexedLedger.DepartmentUUID !== EmptyUUID &&
            indexedLedger.DepartmentUUID !== '' &&
            indexedLedger.InvoiceType === WorkorderInvoiceType.Standard &&
            indexedLedger.VatType === vatType
          ) {
            if (type === 'Revenue') {
              const isRevenueFieldRequired = !allAdministrationLedger?.Revenue && !fallbackLedger?.Revenue && !value

              setFieldValue(`Administrations[${adminIndex}].Ledgers[${ledgerIndex}].IsRevenueFieldRequired`, isRevenueFieldRequired)
            } else if (type === 'Discount') {
              const isDiscountFieldRequired = !allAdministrationLedger?.Discount && !fallbackLedger?.Discount && !value

              setFieldValue(`Administrations[${adminIndex}].Ledgers[${ledgerIndex}].IsDiscountFieldRequired`, isDiscountFieldRequired)
            }
          }
        }
      }
    } else {
      for (let ledgerIndex = 0; ledgerIndex < Administrations[tabIndex].Ledgers.length; ledgerIndex++) {
        const indexedLedger = Administrations[tabIndex].Ledgers[ledgerIndex]
        const allAdministrationLedger = allAdministration?.Ledgers[ledgerIndex]

        if (
          indexedLedger.DepartmentUUID !== EmptyUUID &&
          indexedLedger.DepartmentUUID !== '' &&
          indexedLedger.InvoiceType === WorkorderInvoiceType.Standard &&
          indexedLedger.VatType === vatType
        ) {
          if (type === 'Revenue') {
            const isRevenueFieldRequired = !allDefaultLedger?.Revenue && !allAdministrationLedger?.Revenue && !value

            setFieldValue(`Administrations[${tabIndex}].Ledgers[${ledgerIndex}].IsRevenueFieldRequired`, isRevenueFieldRequired)
          } else if (type === 'Discount') {
            const isDiscountFieldRequired = !allDefaultLedger?.Discount && !allAdministrationLedger?.Discount && !value

            setFieldValue(`Administrations[${tabIndex}].Ledgers[${ledgerIndex}].IsDiscountFieldRequired`, isDiscountFieldRequired)
          }
        }
      }
    }
  }

  const renderFallbackLedger = (vatType: VatType) => {
    const fallbackIndex = Administrations[tabIndex].Ledgers.findIndex(
      ledger => ledger.VatType === vatType && (ledger.DepartmentUUID === EmptyUUID || ledger.DepartmentUUID === '')
    )

    if (fallbackIndex >= 0) {
      return (
        <Grid container item key={`fallback-${fallbackIndex}`} spacing={1}>
          <Grid item xs={12}>
            <Typography variant='subtitle1'>{te('EnumVatType', vatType)}</Typography>
          </Grid>
          <Grid container item spacing={2} xs={12}>
            <Grid item xs={6} sm={3}>
              <Autocomplete
                name={`Administrations[${tabIndex}].Ledgers[${fallbackIndex}].Revenue`}
                label={t('revenue_group.revenue')}
                onChange={(value: string) => {
                  setValidationOnFields('Revenue', value, fallbackIndex, vatType)
                }}
                {...autocompleteLedgerDefaultProps}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Autocomplete
                name={`Administrations.${tabIndex}.Ledgers.${fallbackIndex}.Discount`}
                label={t('revenue_group.discount')}
                onChange={(value: string) => {
                  setValidationOnFields('Discount', value, fallbackIndex, vatType)
                }}
                {...autocompleteLedgerDefaultProps}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Autocomplete
                name={`Administrations[${tabIndex}].Ledgers[${fallbackIndex}].CostPrice`}
                label={t('revenue_group.cost_price')}
                {...autocompleteLedgerDefaultProps}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Autocomplete
                name={`Administrations[${tabIndex}].Ledgers[${fallbackIndex}].Balance`}
                label={t('revenue_group.balance')}
                {...autocompleteLedgerDefaultProps}
              />
            </Grid>
          </Grid>
        </Grid>
      )
    }
    return null
  }

  return (
    <Paper>
      <Grid container spacing={1} p={2}>
        <Grid item xs={12}>
          <Typography variant='h6'>{t('revenue_group.default_sale')}</Typography>
        </Grid>
        {VatLimiter.length === 0 && (
          <Grid item xs={12}>
            <Alert severity='warning'>
              <AlertTitle>{t('revenue_group.no_vat_type_selected')}</AlertTitle>
              {t('revenue_group.select_vat_type')}
            </Alert>
          </Grid>
        )}
        {Administrations[tabIndex].Ledgers.length !== 0 && Administrations[tabIndex].AdministrationCode !== 'notapplicable' && (
          <Grid item xs={12}>
            <Alert severity='info' variant='outlined'>
              <AlertTitle>{t('revenue_group.adminstration_not_authorized')}</AlertTitle>
              {t('revenue_group.authorize_administration_for_ledger')}
            </Alert>
          </Grid>
        )}
        {VatLimiter.sort(sortVATTypes).map(vatType => renderFallbackLedger(vatType))}
      </Grid>
    </Paper>
  )
}

export default FallbackForDepartments
